import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Pencil, Plus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { eur } from "@/lib/format";
import { PageHeader } from "@/pages/admin/AdminLayout";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { code: "", description: "", sale_price: "", purchase_price: "" };
const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));

function PartDialog({ part, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (part) setF(part.id ? { ...part, purchase_price: part.purchase_price ?? "" } : EMPTY); }, [part]);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (num(f.sale_price) == null || Number.isNaN(num(f.sale_price))) return toast.error("Inserisci il prezzo di vendita");
    setBusy(true);
    try {
      const body = { code: f.code.trim(), description: f.description.trim(), sale_price: num(f.sale_price) ?? 0, purchase_price: num(f.purchase_price) };
      const { data } = part.id ? await api.put(`/admin/parts/${part.id}`, body) : await api.post("/admin/parts", body);
      toast.success("Ricambio salvato");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="part-dialog">
        <DialogHeader><DialogTitle>{part?.id ? "Modifica ricambio" : "Nuovo ricambio"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5"><Label>Codice *</Label><Input value={f.code} onChange={upd("code")} data-testid="part-code-input" /></div>
          <div className="space-y-1.5"><Label>Descrizione *</Label><Input value={f.description} onChange={upd("description")} data-testid="part-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prezzo vendita € *</Label><Input type="number" min="0" step="0.01" value={f.sale_price} onChange={upd("sale_price")} data-testid="part-sale-input" /></div>
            <div className="space-y-1.5"><Label>Prezzo acquisto €</Label><Input type="number" min="0" step="0.01" value={f.purchase_price} onChange={upd("purchase_price")} data-testid="part-purchase-input" /></div>
          </div>
          <p className="text-xs text-slate-500">Il prezzo di acquisto lo vede solo l'ufficio.</p>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} disabled={busy} data-testid="part-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoButton({ part, onSaved }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      onSaved((await api.post(`/admin/parts/${part.id}/photo`, fd)).data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={() => ref.current?.click()} className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border bg-slate-50" title="Carica foto" data-testid={`part-photo-${part.code}`}>
      {part.photo_url ? <img src={`${BACKEND}${part.photo_url}`} alt={part.description} className="h-full w-full object-cover" />
        : <ImagePlus className="m-auto h-5 w-5 text-slate-400" />}
      {busy && <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-[#61a229]" />}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </button>
  );
}

export default function AdminParts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get("/parts", { params: { q: q || undefined } }).then((r) => setItems(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const replace = (p) => setItems((xs) => (xs.some((x) => x.id === p.id) ? xs.map((x) => (x.id === p.id ? p : x)) : [p, ...xs]));
  const remove = async (p) => {
    if (!window.confirm(`Eliminare ${p.code} · ${p.description} dal catalogo?`)) return;
    try { await api.delete(`/admin/parts/${p.id}`); setItems((xs) => xs.filter((x) => x.id !== p.id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/parts/import", fd);
      toast.success(`Importati: ${data.created} nuovi, ${data.updated} aggiornati${data.skipped ? `, ${data.skipped} righe scartate` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div data-testid="admin-parts-page">
      <PageHeader eyebrow="Magazzino" title="Catalogo ricambi" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative min-w-60 flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per codice o descrizione" className="pl-9" data-testid="parts-search" /></div>
        <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="parts-import-button">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importa Excel / CSV</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} data-testid="parts-import-input" />
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="parts-add-button"><Plus className="h-4 w-4" /> Nuovo ricambio</Button>
      </div>
      <p className="mb-4 text-xs text-slate-500">File di import: colonne <b>Codice</b>, <b>Descrizione</b>, <b>Prezzo vendita</b> e facoltativa <b>Prezzo acquisto</b>. Se il codice esiste già, il ricambio viene aggiornato.</p>
      <div className="divide-y rounded-2xl border bg-white">
        {items.length ? items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`part-row-${p.code}`}>
            <PhotoButton part={p} onSaved={replace} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{p.description}</p>
              <p className="font-mono text-xs text-slate-500">{p.code}</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-mono font-semibold" data-testid={`part-sale-${p.code}`}>{eur(p.sale_price)}</p>
              {p.purchase_price != null && <p className="font-mono text-xs text-slate-400">acq. {eur(p.purchase_price)}</p>}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setEdit(p)} data-testid={`part-edit-${p.code}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`part-delete-${p.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
          </div>
        )) : <p className="p-8 text-center text-sm text-slate-500" data-testid="parts-empty">Nessun ricambio nel catalogo.</p>}
      </div>
      <PartDialog part={edit} onClose={() => setEdit(null)} onSaved={(p) => { replace(p); setEdit(null); }} />
    </div>
  );
}
